// image-load-order.js
// Pure functions deciding the order binder card images are fetched in.
//
// The book renders a buffer of papers around the current location (see
// BookPages.svelte), and every buffered face sits in the same viewport
// rectangle as the visible spread. Browser lazy-loading therefore treats all
// of them as on-screen and fires every request at once. Instead, each face
// gets a load tier by flip distance and the image-loader store opens tiers one
// at a time: the visible spread, then one flip forward, one flip back, two
// forward, two back, and so on. See IMAGE_LOAD_ORDER.md.

export const IMAGE_TIER = Object.freeze({
  VISIBLE: 0,
  NEXT: 1,
  PREVIOUS: 2,
});

/** Tiers 0..IMAGE_TIER_COUNT-1; the last one collects every farther face. */
export const IMAGE_TIER_COUNT = 8;
const FARTHEST = IMAGE_TIER_COUNT - 1;

/**
 * The location at which a face is on screen. Desktop shows paper L-1's back on
 * the left and paper L's front on the right, so a back face belongs to the
 * location after its paper. Mobile shows fronts only.
 */
function faceLocation(paperIndex, face, isMobile) {
  if (face === 'front') return paperIndex;
  return isMobile ? null : paperIndex + 1;
}

/**
 * Load tier for one face of one paper: 0 for the visible spread, then odd
 * tiers for flips forward and even tiers for flips back, nearest first.
 * @param {{ paperIndex: number, face: 'front'|'back', currentLocation: number, isMobile?: boolean }} args
 */
export function getFaceLoadTier({ paperIndex, face, currentLocation, isMobile = false }) {
  const location = faceLocation(paperIndex, face, isMobile);
  if (location === null) return FARTHEST;
  const delta = location - currentLocation;
  const tier = delta === 0 ? 0 : delta > 0 ? 2 * delta - 1 : -2 * delta;
  return Math.min(tier, FARTHEST);
}

/** True when either face of the paper is part of the visible spread. */
export function isPaperOnScreen({ paperIndex, currentLocation, isMobile = false }) {
  return (
    getFaceLoadTier({ paperIndex, face: 'front', currentLocation, isMobile }) ===
      IMAGE_TIER.VISIBLE ||
    getFaceLoadTier({ paperIndex, face: 'back', currentLocation, isMobile }) === IMAGE_TIER.VISIBLE
  );
}

/**
 * The highest tier allowed to start fetching: the first tier that still has
 * images waiting to start or in flight (unless that tier overran its wait
 * budget and was forced open), or the last tier when nothing is busy.
 * @param {number[]} pending - in-flight image count per tier
 * @param {number[]} waiting - queued-but-not-started image count per tier
 * @param {boolean[]} forced - per tier, true once its wait budget expired
 */
export function computeOpenTier(pending, waiting, forced) {
  for (let tier = 0; tier < IMAGE_TIER_COUNT; tier++) {
    if (waiting[tier] > 0) return tier;
    if (pending[tier] > 0 && !forced[tier]) return tier;
  }
  return FARTHEST;
}
